<script setup lang="ts">
/**
 * Create account (Figma `66:47251` … `66:47467`).
 *
 * Two kinds of email error live side by side here and behave differently:
 *  - malformed address — client-checkable, so it *gates* the button (`66:47520`)
 *  - already registered — only the server knows, so the button stays enabled
 *    and the error clears the moment the email is edited (`66:47517`)
 *
 * The requirement chips never turn red while typing; an unmet rule is simply
 * neutral (`66:47508`). They appear on focus and stay until all five pass.
 */
import { FairButton } from '@/ui/primitives/FairButton'
import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthModal, useAuthStore } from '@/features/auth/store'
import { isEmail, isPasswordValid } from '@/features/auth/model/passwordRules'
import { mapAuthError } from '@/features/auth/model/mapAuthError'
import { useAuthFormValidation } from '@/features/auth/model/useAuthFormValidation'
import AuthField from '@/features/auth/ui/parts/AuthField.vue'
import AuthFooterSwitch from '@/features/auth/ui/parts/AuthFooterSwitch.vue'
import AuthSocialRow from '@/features/auth/ui/parts/AuthSocialRow.vue'
import PasswordRequirementChips from '@/features/auth/ui/parts/PasswordRequirementChips.vue'
import { FairCheckbox } from '@/ui/primitives/FairCheckbox'
import { useFairToast } from '@/ui/patterns/FairToast'
import { PRIVACY_POLICY_ROUTE, TERMS_OF_SERVICE_ROUTE } from '@/router/routes'

const emits = defineEmits(['closeModal'])

const { t } = useI18n()
const toast = useFairToast()
const authModal = useAuthModal()

const firstName = ref('')
const email = ref('')
const password = ref('')
const consent = ref(false)

const passwordFocused = ref(false)
/** Only complain about the address once the user has left the field. */
const emailTouched = ref(false)

const emailFormatInvalid = computed(() =>
  emailTouched.value && email.value.trim() !== '' && !isEmail(email.value),
)

const clientValid = computed(() =>
  firstName.value.trim() !== ''
  && isEmail(email.value)
  && isPasswordValid(password.value)
  && consent.value,
)

const form = useAuthFormValidation({ clientValid })

const emailServerError = computed(() => form.fieldError('email'))

const emailError = computed(() => {
  if (emailServerError.value) return t(emailServerError.value.messageKey)
  if (emailFormatInvalid.value) return t('auth.errors.invalid_email')
  return undefined
})

const chipsVisible = computed(() =>
  passwordFocused.value || (password.value !== '' && !isPasswordValid(password.value)),
)

function handleEmailEdit() {
  form.notifyFieldEdited('email')
}

async function handleSubmit() {
  await form.submit(async () => {
    try {
      // The store moves on to the e-mail verification screen itself. The
      // redesign has no verify step and ends on an "account created" toast
      // instead — but removing verification is a backend call (BE-017), so the
      // existing step stays until that question is answered.
      await useAuthStore().signUp({
        name: firstName.value.trim(),
        email: email.value.trim(),
        password: password.value,
      })
    } catch (error) {
      const mapped = mapAuthError(error, 'signup')
      if (mapped.scope === 'network') {
        toast.show({ message: t(mapped.messageKey), tone: 'warning' })
      } else {
        form.setServerError(mapped)
      }
    }
  })
}
</script>

<template>
  <div class="fair-auth-panel">
    <form
      class="fair-auth-form"
      novalidate
      @submit.prevent="handleSubmit"
    >
      <AuthField
        v-model="firstName"
        input-id="auth-signup-first-name"
        autocomplete="given-name"
        :label="t('auth.signup.first_name_label')"
      />

      <AuthField
        v-model="email"
        input-id="auth-signup-email"
        type="email"
        autocomplete="email"
        :label="t('auth.signup.email_label')"
        :invalid="Boolean(emailError)"
        :error-text="emailError"
        @update:model-value="handleEmailEdit"
        @blur="emailTouched = true"
      />

      <div class="fair-auth-form__field">
        <AuthField
          v-model="password"
          input-id="auth-signup-password"
          type="password"
          autocomplete="new-password"
          :label="t('auth.signup.password_label')"
          @focus="passwordFocused = true"
          @blur="passwordFocused = false"
        />
        <PasswordRequirementChips
          :password="password"
          :visible="chipsVisible"
        />
      </div>

      <div class="fair-auth-consent">
        <FairCheckbox
          v-model="consent"
          input-id="auth-signup-consent"
          :show-label="false"
          :aria-label="t('auth.signup.tos_prefix')"
        />
        <label
          class="fair-auth-consent__label"
          for="auth-signup-consent"
        >
          {{ t('auth.signup.tos_prefix') }}
          <RouterLink
            class="fair-auth-consent__link"
            :to="PRIVACY_POLICY_ROUTE"
            target="_blank"
            rel="noopener noreferrer"
            @click.stop
          >
            {{ t('auth.signup.tos_privacy') }}
          </RouterLink>
          {{ t('auth.signup.tos_and') }}
          <RouterLink
            class="fair-auth-consent__link"
            :to="TERMS_OF_SERVICE_ROUTE"
            target="_blank"
            rel="noopener noreferrer"
            @click.stop
          >
            {{ t('auth.signup.tos_terms') }}
          </RouterLink>
        </label>
      </div>

      <FairButton
      class="fair-auth-cta"
      size="medium"
        type="submit"
        :label="t('auth.signup.submit')"
        :disabled="form.submitDisabled.value"
      />
    </form>

    <AuthSocialRow @signed-in="emits('closeModal')" />

    <AuthFooterSwitch
      :prompt="t('auth.signup.have_account')"
      :action-label="t('auth.signup.login_link')"
      @switch="authModal.showAuthModal('login')"
    />
  </div>
</template>
